import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BookMarked,
  Check,
  CheckCircle2,
  LockKeyhole,
  Mail,
  Phone,
} from "lucide-react";
import { api } from "../lib/api";

const initialForm = {
  name: "",
  work_email: "",
  agency: "",
  role: "",
  team_size: "",
  current_pain: "",
  preferred_contact: "email",
  phone: "",
  consent: false,
  source: "commercial_landing",
};

const Field = ({ label, hint, error, children }) => (
  <label className="block">
    <span className="flex items-end justify-between gap-3 text-sm font-medium text-[#171717]">
      {label}
      {hint && <span className="text-xs font-normal text-black/40">{hint}</span>}
    </span>
    <span className="mt-2 block">{children}</span>
    {error && <span className="mt-1.5 block text-xs text-[#A12B2B]">{error}</span>}
  </label>
);

const inputClass = "w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-sm text-[#171717] outline-none transition focus:border-black/50 focus:ring-2 focus:ring-black/5";

export default function RequestPilot() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setSubmitError("");
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.work_email)) next.work_email = "Enter a valid work email.";
    if (form.agency.trim().length < 2) next.agency = "Please enter your agency or consultancy.";
    if (form.role.trim().length < 2) next.role = "Please enter your role.";
    if (!form.team_size) next.team_size = "Select your team size.";
    if (form.current_pain.trim().length < 20) next.current_pain = "Tell us a little more so we can prepare a useful conversation.";
    if (form.preferred_contact === "phone" && !form.phone.trim()) next.phone = "Enter the number you would like us to use.";
    if (!form.consent) next.consent = "We need permission to respond to your request.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await api.requestPilot({
        ...form,
        name: form.name.trim(),
        work_email: form.work_email.trim(),
        agency: form.agency.trim(),
        role: form.role.trim(),
        current_pain: form.current_pain.trim(),
        phone: form.phone.trim() || null,
      });
      setResult(response);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const validation = error?.response?.status === 422;
      setSubmitError(validation
        ? "A few details need another look. Please review the form and try again."
        : "We could not save your request. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F2EA] text-[#171717]" data-testid="request-pilot-page">
      <header className="border-b border-black/10">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-5 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-[#171717] text-white"><BookMarked className="h-4 w-4" /></div>
            <div className="font-display text-xl">Commitment Ledger</div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-black/55 hover:text-black"><ArrowLeft className="h-4 w-4" /> Back to overview</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] px-6 py-14 lg:px-10 lg:py-20">
        {result ? (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-2xl rounded-3xl border border-black/10 bg-[#FCFBF7] p-8 text-center shadow-[0_25px_70px_rgba(23,23,23,0.08)] md:p-14" data-testid="pilot-success">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#E3F2EA] text-[#176B49]"><CheckCircle2 className="h-7 w-7" /></div>
            <div className="mt-7 text-[11px] uppercase tracking-[0.2em] text-black/40">Request received</div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl">{result.already_received ? "You are already on our list." : "Thanks. Let’s make this useful."}</h1>
            <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-black/60">
              We will review your context and reply within two business days with either a short qualification question or a time for an initial conversation.
            </p>
            <div className="mt-8 rounded-xl bg-[#F1EEE5] p-5 text-left">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-black/45">What happens next</div>
              <ol className="mt-3 space-y-2 text-sm text-black/65">
                <li>1. We confirm whether the assisted pilot fits your team.</li>
                <li>2. We agree on one account manager and two client relationships.</li>
                <li>3. We run a focused four-week pilot with weekly reviews.</li>
              </ol>
            </div>
            <Link to="/app" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#171717] px-5 py-3 text-sm font-medium text-white hover:bg-[#2B2B2B]">Explore the product <ArrowRight className="h-4 w-4" /></Link>
          </motion.section>
        ) : (
          <div className="grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20">
            <motion.aside initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:sticky lg:top-10 lg:self-start">
              <div className="text-[11px] uppercase tracking-[0.22em] text-black/45">Four-week assisted pilot</div>
              <h1 className="mt-4 font-display text-5xl leading-[1.02] md:text-6xl">Bring one client relationship into focus.</h1>
              <p className="mt-6 text-base leading-relaxed text-black/60">This is for agencies and consultancies that feel important commitments slipping between client conversations, account meetings, and internal follow-up.</p>

              <div className="mt-9 space-y-4 border-y border-black/10 py-7">
                {[
                  "One account manager and two client relationships",
                  "Selected conversations only, no full inbox access",
                  "Weekly review of changes, actions, and false positives",
                  "A clear paid continuation decision after four weeks",
                ].map((item) => (
                  <div key={item} className="flex gap-3 text-sm text-black/65"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#176B49]" /> {item}</div>
                ))}
              </div>

              <div className="mt-7 flex gap-3 rounded-xl bg-[#EAE5D8] p-4 text-sm leading-relaxed text-black/60">
                <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
                Do not include client names, conversation text, financial details, or other confidential information in this form.
              </div>
            </motion.aside>

            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.06 }} className="rounded-2xl border border-black/10 bg-[#FCFBF7] p-6 shadow-[0_25px_70px_rgba(23,23,23,0.07)] md:p-9" noValidate data-testid="pilot-form">
              <div className="border-b border-black/10 pb-6">
                <h2 className="font-display text-3xl">Tell us about your team</h2>
                <p className="mt-2 text-sm text-black/50">Enough context to prepare a useful first conversation. Nothing more.</p>
              </div>

              <div className="mt-7 grid gap-6 sm:grid-cols-2">
                <Field label="Your name" error={errors.name}><input value={form.name} onChange={(e) => update("name", e.target.value)} className={inputClass} autoComplete="name" data-testid="pilot-name" /></Field>
                <Field label="Work email" error={errors.work_email}><input type="email" value={form.work_email} onChange={(e) => update("work_email", e.target.value)} className={inputClass} autoComplete="email" data-testid="pilot-email" /></Field>
                <Field label="Agency or consultancy" error={errors.agency}><input value={form.agency} onChange={(e) => update("agency", e.target.value)} className={inputClass} autoComplete="organization" data-testid="pilot-agency" /></Field>
                <Field label="Your role" error={errors.role}><input value={form.role} onChange={(e) => update("role", e.target.value)} className={inputClass} autoComplete="organization-title" data-testid="pilot-role" /></Field>
                <Field label="Team size" hint="People in the business" error={errors.team_size}>
                  <select value={form.team_size} onChange={(e) => update("team_size", e.target.value)} className={inputClass} data-testid="pilot-team-size">
                    <option value="">Select a range</option><option value="1-9">1 to 9</option><option value="10-24">10 to 24</option><option value="25-49">25 to 49</option><option value="50-100">50 to 100</option><option value="101+">101+</option>
                  </select>
                </Field>
                <Field label="Preferred contact" error={errors.preferred_contact}>
                  <div className="grid grid-cols-2 gap-2">
                    {["email", "phone"].map((method) => (
                      <button key={method} type="button" onClick={() => update("preferred_contact", method)} className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm capitalize ${form.preferred_contact === method ? "border-[#171717] bg-[#171717] text-white" : "border-black/15 bg-white"}`}>
                        {method === "email" ? <Mail className="h-4 w-4" /> : <Phone className="h-4 w-4" />} {method}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              {form.preferred_contact === "phone" && <div className="mt-6"><Field label="Phone number" error={errors.phone}><input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} autoComplete="tel" data-testid="pilot-phone" /></Field></div>}

              <div className="mt-6">
                <Field label="Where do important commitments currently get lost?" hint={`${form.current_pain.length}/1200`} error={errors.current_pain}>
                  <textarea value={form.current_pain} onChange={(e) => update("current_pain", e.target.value.slice(0, 1200))} rows={5} className={`${inputClass} resize-y`} placeholder="Describe the workflow problem without naming clients or pasting conversation content." data-testid="pilot-pain" />
                </Field>
              </div>

              <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-black/10 bg-[#F5F2EA] p-4">
                <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-1 h-4 w-4 accent-[#171717]" data-testid="pilot-consent" />
                <span className="text-sm leading-relaxed text-black/60">I agree that Commitment Ledger may contact me about this pilot request. My details will be used for pilot qualification and follow-up, not sold or added to unrelated marketing lists.</span>
              </label>
              {errors.consent && <div className="mt-1.5 text-xs text-[#A12B2B]">{errors.consent}</div>}

              {submitError && <div className="mt-5 rounded-xl border border-[#D96C6C]/35 bg-[#FCEEEE] p-4 text-sm text-[#8F2525]" data-testid="pilot-submit-error">{submitError}</div>}

              <button type="submit" disabled={submitting} className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#171717] px-5 py-3.5 text-sm font-medium text-white hover:bg-[#2B2B2B] disabled:cursor-wait disabled:opacity-60" data-testid="pilot-submit">
                {submitting ? "Saving your request…" : "Request a pilot"} {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
              <p className="mt-3 text-center text-xs text-black/40">We normally reply within two business days.</p>
            </motion.form>
          </div>
        )}
      </main>
    </div>
  );
}
